/**
 * Toast utility with deduplication to prevent multiple identical toasts
 */
import { toast as sonnerToast } from 'sonner';

// Track shown toasts with timestamps
const shownToasts = new Map();
const DEDUP_WINDOW_MS = 3000; // 3 seconds deduplication window

/**
 * Clean up old toast entries
 */
const cleanupOldToasts = () => {
  const now = Date.now();
  for (const [key, timestamp] of shownToasts.entries()) {
    if (now - timestamp > DEDUP_WINDOW_MS) {
      shownToasts.delete(key);
    }
  }
};

/**
 * Check if a toast should be shown (not a duplicate)
 */
const shouldShowToast = (message, type) => {
  cleanupOldToasts();
  const key = `${type}:${message}`;
  if (shownToasts.has(key)) {
    return false;
  }
  shownToasts.set(key, Date.now());
  return true;
};

/**
 * Deduplicated toast functions
 */
export const toast = {
  error: (message, options) => {
    if (shouldShowToast(message, 'error')) {
      return sonnerToast.error(message, options);
    }
  },
  success: (message, options) => {
    if (shouldShowToast(message, 'success')) {
      return sonnerToast.success(message, options);
    }
  },
  info: (message, options) => {
    if (shouldShowToast(message, 'info')) {
      return sonnerToast.info(message, options);
    }
  },
  warning: (message, options) => {
    if (shouldShowToast(message, 'warning')) {
      return sonnerToast.warning(message, options);
    }
  },
  // For cases where you explicitly want to show duplicate toasts
  force: {
    error: (message, options) => sonnerToast.error(message, options),
    success: (message, options) => sonnerToast.success(message, options),
    info: (message, options) => sonnerToast.info(message, options),
    warning: (message, options) => sonnerToast.warning(message, options),
  },
  // Pass through other sonner methods
  dismiss: sonnerToast.dismiss,
  promise: sonnerToast.promise,
  custom: sonnerToast.custom,
  message: sonnerToast.message,
  loading: sonnerToast.loading,
};

export default toast;
